import { Component, Input } from '@angular/core'
import { CommonModule } from '@angular/common'

/**
 * Defines the available sizes for the avatar.
 */
export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl'

/**
 * Defines the possible states for the avatar.
 */
export type AvatarState = 'active' | 'hover' | 'focused' | 'inactive'

@Component({
  selector: 'hyperkit-avatar',
  standalone: true,
  imports: [CommonModule], // Import CommonModule for directives like ngClass
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
})
export class AvatarComponent {
  /**
   * The size of the avatar. Defaults to 'md'.
   */
  @Input() public size: AvatarSize = 'md'

  /**
   * The state of the avatar. Defaults to 'active'.
   */
  @Input() public state: AvatarState = 'active'

  /**
   * The source URL for the avatar image.
   */
  @Input() public src?: string

  /**
   * Alternative text for the avatar image.
   */
  @Input() public alt?: string

  /**
   * The initials to display when the image is not available.
   */
  @Input() public initials?: string

  /**
   * Additional CSS classes for custom styling.
   */
  @Input() public additionalClass?: string

  /**
   * Tracks whether the image has failed to load.
   */
  public imageError = false

  /**
   * Determines whether to display initials instead of the image.
   * @returns `true` if there is no image source or the image has failed to load.
   */
  public get showInitials(): boolean {
    return !this.src || this.imageError
  }

  /**
   * Handles the image load error by setting the `imageError` flag to true.
   */
  public onImageError(): void {
    this.imageError = true
  }
}
